import './page.scss';

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import contains from 'utils/contains';

import {
  hasPageOverlays,
  getPageOverlay,
  getAssetUrl,
  isPhotoPlaceholder,
  isVectorPlaceholder,
  isTextPlaceholder,
  isStaticPlaceholder,
  isBackgroundPlaceholder,
} from 'editor-utils/template';
import TOC from 'editor-utils/toc';

import withPageStyle, { PagePropTypes } from './index';
import PhotoPlaceholder from '../placeholder/photo';
import TextPlaceholder from '../placeholder/text';
import VectorPlaceholder from '../placeholder/vector';
import StaticPlaceholder from '../placeholder/static';
import PageLayer from '../placeholder/layer';
import { usePhotosQuality } from '../photoQualityAlert';
import BleedZone from './bleed';

const PagePreview = props => {
  const { page } = props;

  const [photosQuality, handleOnPhotoQuality] = usePhotosQuality(
    props.template ? props.template.template.minDPI : undefined,
  );

  const textExceedingRef = useRef({});
  const handleTextExceeds = (placeholderId, textExceeds) => {
    if (textExceeds) {
      textExceedingRef.current[placeholderId] = true;
    } else {
      delete textExceedingRef.current[placeholderId];
    }

    const someTextExceedsOnPage = Object.keys(textExceedingRef.current).length > 0;
    if (props.onTextExceeds) {
      props.onTextExceeds(someTextExceedsOnPage);
    }
  };

  useEffect(() => {
    let quality = 1;
    if (Object.keys(photosQuality).length > 0) {
      quality = Math.min(...Object.entries(photosQuality).map(([, value]) => value));
    }
    if (props.onPhotosQuality) {
      props.onPhotosQuality(quality);
    }
  }, [props.onPhotosQuality, photosQuality]);

  const handleOnPhotoUload = placeholder => {
    props.onPhotoUpload(placeholder);
  };

  const handleOnPhotoEdit = (placeholder, photo) => {
    props.onPhotoEdit(placeholder, photo);
  };

  const handleOnTextChange = (placeholder, value, override = {}) => {
    if (props.onTextChange) {
      props.onTextChange(placeholder, value, override);
    }
  };

  const handleOnPlaceholderRemove = placeholder => {
    if (props.onPlaceholderRemove) {
      props.onPlaceholderRemove(placeholder);
    }
  };

  let pageOverlay;
  if (hasPageOverlays(page)) {
    // TODO we need to support more types of overlays
    pageOverlay = getPageOverlay(page);
  }

  return (
    <>
      {page.placeholders
        .reduce(
          (acc, placeholder) => {
            const layerIndex = placeholder.layer ? 2 : 0;

            const data = placeholder.data ?? {};
            const override = placeholder.override ?? {};

            if (isBackgroundPlaceholder(placeholder)) {
              const photo = {
                url: data.photo_url,
              };

              acc[layerIndex].push(
                <PhotoPlaceholder
                  key={placeholder.id}
                  placeholder={{ ...placeholder, ...override }}
                  photo={{ ...photo, ...data }}
                  page={page}
                  pageSizeScale={props.pageSizeScale}
                  highQualityImage={props.withQualityCheck}
                  template={props.template}
                  preview={props.preview}
                />,
              );
            } else if (isPhotoPlaceholder(placeholder)) {
              const photo = props.photos.find(p => p.key === data.photo_key);
              const tags = photo?.tags ?? [];

              acc[layerIndex].push(
                <PhotoPlaceholder
                  key={placeholder.id}
                  placeholder={{ ...placeholder, ...override }}
                  photo={photo ? { ...photo, ...data } : undefined}
                  page={page}
                  pageSizeScale={props.pageSizeScale}
                  highQualityImage={props.withQualityCheck}
                  template={props.template}
                  preview={props.preview}
                  disableImageTransform={contains(tags, ['layer'])}
                />,
              );
            } else if (isTextPlaceholder(placeholder)) {
              // When page is interactive, we render text after the pageOverlay - clickable
              if (!props.interactive) {
                acc[layerIndex].push(
                  <TextPlaceholder
                    key={placeholder.id}
                    placeholder={{ ...placeholder, ...override }}
                    page={page}
                    template={props.template}
                    text={data.value ?? ''}
                    pageSizeScale={props.pageSizeScale}
                    preview={props.preview}
                    onChange={handleOnTextChange}
                  />,
                );
              }
            } else if (isVectorPlaceholder(placeholder)) {
              acc[layerIndex].push(
                <VectorPlaceholder
                  key={placeholder.id}
                  placeholder={{ ...placeholder, ...override }}
                  page={page}
                  template={props.template}
                  pageSizeScale={props.pageSizeScale}
                  interactive={false}
                  onRemove={handleOnPlaceholderRemove}
                />,
              );
            } else if (isStaticPlaceholder(placeholder)) {
              acc[layerIndex].push(
                <StaticPlaceholder
                  key={placeholder.id}
                  placeholder={{ ...placeholder, ...override }}
                  page={page}
                  template={props.template}
                />,
              );
            }

            return acc;
          },
          [
            // 0 - base layer
            [],
            // 1 - pageOverlay
            [
              pageOverlay ? (
                <PageLayer
                  key="overlay"
                  src={`${getAssetUrl(
                    props.template,
                    `/assets_client/pages/${pageOverlay}.png`,
                  )}?_`}
                  className="editor-page-overlay"
                />
              ) : null,
            ],
            // 2 - other layers
            [],
          ],
        )
        .flat()}

      {props.interactive &&
        page.placeholders.map(placeholder => {
          const data = placeholder.data ?? {};
          const override = placeholder.override ?? {};

          if (isPhotoPlaceholder(placeholder)) {
            const photo = props.photos.find(p => p.key === data.photo_key);

            return (
              <PhotoPlaceholder
                key={placeholder.id}
                placeholder={{ ...placeholder, ...override }}
                photo={photo ? { ...photo, ...data } : undefined}
                page={page}
                template={props.template}
                link
                highQualityImage={props.withQualityCheck}
                withQualityCheck={props.withQualityCheck}
                onUpload={handleOnPhotoUload}
                onEdit={handleOnPhotoEdit}
                photoQuality={photoQuality => handleOnPhotoQuality(placeholder.id, photoQuality)}
              />
            );
          }

          if (isTextPlaceholder(placeholder)) {
            return (
              <TextPlaceholder
                key={placeholder.id}
                placeholder={{ ...placeholder, ...override }}
                page={page}
                template={props.template}
                text={data.value ?? ''}
                pageSizeScale={props.pageSizeScale}
                interactive={!(page.isTOC && TOC.isPageNumberPlaceholder(placeholder))}
                preview={props.preview}
                onChange={handleOnTextChange}
                onTextExceeds={textExceeds => handleTextExceeds(placeholder.id, textExceeds)}
              />
            );
          }

          return null;
        })}

      {props.bleed && (
        <BleedZone sku={props.template.id} pageWidth={page.size.width} pageHeight={page.size.height} value={props.bleed} />
      )}
    </>
  );
};

PagePreview.propTypes = {
  ...PagePropTypes,

  interactive: PropTypes.bool,
  bleed: PropTypes.array,
  preview: PropTypes.bool,
  photos: PropTypes.array,
  withQualityCheck: PropTypes.bool,

  onPhotoEdit: PropTypes.func,
  onPhotoUpload: PropTypes.func,
  onTextChange: PropTypes.func,
  onTextExceeds: PropTypes.func,
  onPhotosQuality: PropTypes.func,
  onPlaceholderRemove: PropTypes.func,
};

PagePreview.defaultProps = {
  photos: [],
  withQualityCheck: false,

  interactive: false,
  preview: false,
};

export default withPageStyle(PagePreview);
